import { useCreatePaymentSessionMutation } from '@features/Checkout/services/checkout';
import { captureException, setExtras } from '@sentry/nextjs';
import { useEffect, useState } from 'react';

interface UsePayment {
  walletId?: boolean;
  paymentType?: number;
  membershipsId?: string[];
  terminalSession?: boolean;
  couponCode?: boolean;
  skip?: boolean;
  serviceId?: string;
  skipMembershipValidation?: boolean;
}

const usePayment = ({
  walletId = false,
  paymentType = 1,
  membershipsId = [],
  terminalSession = false,
  couponCode = false,
  skip = false,
  serviceId = '',
  skipMembershipValidation = false,
}: UsePayment) => {
  const [paymentSession, setPaymentSession] = useState<{
    session: string;
    type: number;
  } | null>(null);
  const [
    createPaymentSession,
    {
      isError: failedCreatingPaymentSession,
      isLoading: isCreatingPaymentSession,
    },
  ] = useCreatePaymentSessionMutation();

  useEffect(() => {
    if (!walletId || skip) return;
    const createPayment = async () => {
      try {
        const { paymentSession: res } = await createPaymentSession({
          wallet: walletId,
          type: paymentType,
          ...(membershipsId && {
            membershipsid: membershipsId,
          }),
          ...(terminalSession && { terminalSession }),
          ...(couponCode && {
            coupons: [couponCode],
          }),
          ...(serviceId && {
            serviceUnitUid: serviceId,
          }),
          ...(skipMembershipValidation && {
            skip_mebership_validation: skipMembershipValidation,
          }),
        }).unwrap();

        setPaymentSession(res);
      } catch (error: any) {
        const err = new Error(`Create payment session failed`);
        setExtras(error);
        captureException(err);
      }
    };

    createPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletId, skip]);

  return {
    sessionId: paymentSession?.session,
    sessionType: paymentSession?.type,
    isLoading: isCreatingPaymentSession,
    isSuccess: !failedCreatingPaymentSession,
  };
};

export default usePayment;
